"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import StatCard from "@/components/ui/StatCard";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Badge from "@/components/ui/badge/Badge";
import { rmcClient } from "@/lib/rmcClient";
import { useQuery } from "@tanstack/react-query";
import React from "react";

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function RmcStock() {
  const [mat, setMat] = React.useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["stockOverview"],
    queryFn: async () => (await rmcClient.getStockOverview()).data,
  });
  const { data: ledger = [] } = useQuery({
    queryKey: ["stockLedger", mat?.material_id],
    queryFn: async () =>
      mat ? (await rmcClient.getStockLedger(mat.material_id)).data : [],
    enabled: !!mat?.material_id,
  });

  const rows = React.useMemo(() => {
    // Supports both payloads:
    // 1) { rows: [...] } (old shape)
    // 2) [{ material: {...}, total_in, total_out, balance }, ...] (current API)
    const base = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
    return base.map((r) => {
      const materialName = r.material?.name ?? r.material ?? "—";
      const materialId = r.material?.id ?? r.material_id ?? null;
      const uom = r.material?.uom ?? r.uom ?? "—";
      const totalIn = asNumber(r.total_in);
      const totalOut = asNumber(r.total_out);
      const balance = asNumber(r.balance);
      const status =
        r.status ??
        (balance < 0
          ? { color: "red", label: "Negative" }
          : balance === 0
            ? { color: "yellow", label: "Zero" }
            : { color: "green", label: "In stock" });
      return {
        ...r,
        material: materialName,
        material_id: materialId,
        uom,
        total_in: totalIn,
        total_out: totalOut,
        balance,
        status,
      };
    });
  }, [data]);

  const todayInwardValue = React.useMemo(() => {
    if (data && !Array.isArray(data) && data.today_inward != null) {
      return `${asNumber(data.today_inward).toFixed(1)} TON`;
    }
    if (!rows.length) return "—";
    return `${rows.reduce((sum, r) => sum + asNumber(r.total_in), 0).toFixed(1)} TON`;
  }, [data, rows]);

  const totalMaterialsValue =
    data && !Array.isArray(data) && data.total_materials != null ? data.total_materials : rows.length || "—";
  const lowCountValue =
    data && !Array.isArray(data) && data.low_count != null
      ? data.low_count
      : rows.filter((r) => asNumber(r.balance) <= 0).length || "—";

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Current Stock" />
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Low Stock Materials" value={lowCountValue} />
        <StatCard label="Total Materials" value={totalMaterialsValue} />
        <StatCard label="Total Inward" value={todayInwardValue} />
      </div>
      <DataTable
        columns={[
          { key: "material", header: "Material" },
          { key: "uom", header: "UOM" },
          { key: "tin", header: "Total IN", render: (r) => asNumber(r.total_in).toFixed(3) },
          { key: "tout", header: "Total OUT", render: (r) => asNumber(r.total_out).toFixed(3) },
          { key: "bal", header: "Balance", render: (r) => asNumber(r.balance).toFixed(3) },
          {
            key: "st",
            header: "Status",
            render: (r) => (
              <Badge
                color={
                  r.status.color === "red"
                    ? "error"
                    : r.status.color === "yellow"
                      ? "warning"
                      : "success"
                }
              >
                {r.status.label}
              </Badge>
            ),
          },
        ]}
        data={rows}
        isLoading={isLoading}
        onRowClick={(r) => setMat(r)}
      />
      <DrawerPanel
        open={!!mat}
        onClose={() => setMat(null)}
        title={`Movement — ${mat?.material || ""}`}
      >
        <DataTable
          columns={[
            { key: "date", header: "Date" },
            { key: "type", header: "Type" },
            { key: "qty", header: "Qty", render: (r) => r.qty?.toFixed(3) },
            { key: "ref", header: "Ref" },
          ]}
          data={ledger}
          isLoading={false}
        />
      </DrawerPanel>
    </div>
  );
}
